// Pure, engine-free derivation for the mobile drag-glide (a straight-line multi-cell drag resolved to
// a deterministic sequence of single-step moves) and its wall/edge/armed-decoy clamp.
//
// This lives OUTSIDE GameScene so it is (a) single-sourced — the renderer AND the input smoke import
// the SAME functions, so a test can never drift from shipped behavior — and (b) unit-testable
// headlessly (no Phaser). It is RENDERER-side, not Core: it reads Core state and calls the EXPORTED
// Core pure helpers (neighborId/passable), never mutates state, and adds no verb. The clamp is
// single-sourced from Core's own move rule (rules.ts) via passable(), so it cannot diverge from the
// simulation (e.g. it honors the live armed-decoy lockout, not just walls).
import type { BoardState, Direction } from '../core';
import { neighborId, passable } from '../core';

/** A drag is "straight" iff cross <= primary * ratio (within ~atan(ratio) ≈ 26.57° of an axis at 0.5). */
export const STRAIGHT_RATIO = 0.5;

/** Dominant-axis direction for a drag delta. Ties (|dx| === |dy|) resolve to the vertical axis (legacy swipe). */
export function dominantDir(dx: number, dy: number): Direction {
  return Math.abs(dx) > Math.abs(dy) ? (dx > 0 ? 'right' : 'left') : dy > 0 ? 'down' : 'up';
}

/** Whether a drag is straight enough to become a multi-step glide (vs. a single dominant-axis step). */
export function isStraightDrag(dx: number, dy: number, ratio = STRAIGHT_RATIO): boolean {
  const primary = Math.max(Math.abs(dx), Math.abs(dy));
  const cross = Math.min(Math.abs(dx), Math.abs(dy));
  return cross <= primary * ratio;
}

/**
 * Number of cells a drag spans along its dominant axis, clamped to [1, axisLen]. A non-straight
 * (diagonal-ish) drag always yields 1 — a single dominant-axis step, preserving today's swipe.
 */
export function glideStepCount(
  dx: number,
  dy: number,
  cellW: number,
  cellH: number,
  cols: number,
  rows: number,
  ratio = STRAIGHT_RATIO,
): number {
  if (!isStraightDrag(dx, dy, ratio)) return 1;
  const dir = dominantDir(dx, dy);
  const horiz = dir === 'left' || dir === 'right';
  const cell = horiz ? cellW : cellH;
  const axisLen = horiz ? cols : rows;
  const primary = Math.max(Math.abs(dx), Math.abs(dy));
  return Math.min(Math.max(Math.round(primary / cell), 1), axisLen);
}

/**
 * Walk up to `n` single steps from `startPos` in `dir`, stopping at the grid edge or the first
 * impassable cell (wall / armed-decoy) EXACTLY as Core's move rule would. Returns the reachable path
 * (the cell ids actually enterable, in order) and the blocking cell id (or null when the grid edge —
 * not a cell — stopped it). Pure: reads state, mutates nothing.
 */
export function clampGlidePath(
  state: BoardState,
  startPos: number,
  dir: Direction,
  n: number,
): { path: number[]; blocked: number | null } {
  const path: number[] = [];
  let cur = startPos;
  for (let i = 0; i < n; i++) {
    const nx = neighborId(cur, dir, state.rows, state.cols);
    if (nx === null) return { path, blocked: null };
    if (!passable(state, nx)) return { path, blocked: nx };
    path.push(nx);
    cur = nx;
  }
  return { path, blocked: null };
}
